import {
  Children,
  PropsWithChildren,
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import styled from "styled-components";
import {
  BottomSheetListWrapper as BottomSheetListWrapperBase,
  opacity00,
  primary01,
  shade800,
  shade850,
  zIndex,
} from "@songtradr-one/components-frontend";
import { useModalConfirm } from "../../context/modal-confirm-context/ModalConfirmContext";
import { staticText } from "../../statics/static-text";

// TEXT
export const text = staticText.wizard;

// STYLES
export const WizardOverlay = styled.div`
  inset: 0;
  z-index: ${zIndex.overlay};
  position: fixed;
  background-color: ${opacity00};
`;

export const Header = styled.div`
  gap: 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 1.5rem 1.5rem 1.5rem;
  text-align: center;
  margin-top: 2rem;
`;

export const Footer = styled.div`
  display: flex;
  padding: 1.5rem;
  justify-content: space-between;
  align-items: center;
`;

export const CenterActions = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const RightButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

export const MobileButtonsRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid ${shade800};
  padding: 1rem 1.5rem 1rem 0rem;
`;

export const ProgressBar = styled.div`
  height: 0.125rem;
  background: ${shade850};
`;

export const ProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background: ${primary01};
  transition: width 0.3s ease;
`;

// max-height leaves a small gutter so the modal never touches the viewport edge.
export const Wrapper = styled.div<{ $hidden?: boolean }>`
  display: flex;
  flex-direction: column;
  height: inherit;
  max-height: calc(100vh - 2rem);
  overflow: hidden;
  margin-bottom: -1.5rem;
  visibility: ${({ $hidden }) => ($hidden ? "hidden" : "visible")};
`;

// Own scroll boundary for step content specifically, so a tall step scrolls internally
// instead of growing the modal past the viewport and pushing the footer off-screen.
export const StepsScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

export const BottomSheetListWrapper = styled(BottomSheetListWrapperBase)`
  flex: 1;
  min-height: 0;
  padding: 0.75rem 1.5rem;
`;

// DATA

// TYPES
export type PageConfig = {
  title: string;
  subtitle: string;
  canContinue: boolean;
  canGoBack?: boolean;
  continueButtonText?: string;
  actions?: ReactNode;
  onContinue?: () => Promise<void> | void;
  /** Lets a page handle back itself (e.g. leaving a sub-view) instead of stepping back */
  onBack?: () => void;
};

// WizardPage reports its config (title, canContinue, etc.) to the Wizard via context
// because Wizard's direct children are wrapper components (e.g. <PrepareIngestion />),
// not <WizardPage /> itself. Since the config depends on each page's internal state
// (form validity, loading, etc.), the child-to-parent reporting pattern is necessary.
export type WizardContextValue = {
  reportConfig: (config: PageConfig) => void;
};

export type Props = PropsWithChildren<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void | Promise<void>;
  onCancel?: () => void;
  confirmCancel?: boolean | string;
  continueButtonText?: string;
  backButtonText?: string;
  cancelButtonText?: string;
  completeButtonText?: string;
  canComplete?: boolean;
  loading?: boolean;
}>;

export type WizardConfig = PropsWithChildren<{
  defaultContinueButtonText?: string;
  defaultBackButtonText?: string;
  defaultCancelButtonText?: string;
  defaultCompleteButtonText?: string;
}>;

export type UseWizardReturn = {
  isOpen: boolean;
  openWizard: (config: WizardConfig) => Promise<boolean>;
  wizardProps: Props;
};

// CONSTANTS

// FUNCTIONS
export const WizardContext = createContext<WizardContextValue | null>(null);
export const WizardStepContext = createContext<{ isActive: boolean }>({
  isActive: false,
});

export const useWizardController = ({
  open,
  onOpenChange,
  children,
  onComplete,
  onCancel,
  confirmCancel,
}: Props) => {
  const { confirm } = useModalConfirm();
  const [currentStep, setCurrentStep] = useState(0);
  const [pageConfig, setPageConfig] = useState<PageConfig | null>(null);

  const childArray = useMemo(() => Children.toArray(children), [children]);

  const reportConfig = useCallback((config: PageConfig) => {
    setPageConfig((prev) => {
      if (
        prev &&
        prev.title === config.title &&
        prev.subtitle === config.subtitle &&
        prev.canContinue === config.canContinue &&
        prev.canGoBack === config.canGoBack &&
        prev.continueButtonText === config.continueButtonText &&
        prev.actions === config.actions &&
        prev.onContinue === config.onContinue &&
        prev.onBack === config.onBack
      )
        return prev;
      return config;
    });
  }, []);

  const contextValue = useMemo(() => ({ reportConfig }), [reportConfig]);

  const isLastStep = currentStep === childArray.length - 1;
  const isFirstStep = currentStep === 0;
  const progress =
    childArray.length > 1 ? (currentStep / (childArray.length - 1)) * 100 : 0;
  const canContinue = pageConfig?.canContinue ?? false;
  const canGoBack =
    Boolean(pageConfig?.onBack) ||
    (!isFirstStep && pageConfig?.canGoBack !== false);

  const handleCancel = async () => {
    if (confirmCancel) {
      const confirmed = await confirm({
        title:
          typeof confirmCancel === "string"
            ? confirmCancel
            : text.confirmCancel,
        confirmLabel: text.confirmCancelConfirm,
        cancelLabel: text.confirmCancelCancel,
      });
      if (!confirmed) return;
    }
    onCancel?.();
    onOpenChange(false);
    setCurrentStep(0);
  };

  const handleBack = () => {
    if (pageConfig?.onBack) return pageConfig.onBack();
    setCurrentStep((prev) => prev - 1);
  };

  const handleContinue = async () => {
    try {
      await pageConfig?.onContinue?.();
    } catch {
      return;
    }
    if (isLastStep) {
      try {
        await onComplete();
      } catch {
        /**
         * In the case of an error, we don't want to close the wizard
         */
        return;
      }
      onOpenChange(false);
      setCurrentStep(0);
    } else setCurrentStep((prev) => prev + 1);
  };

  const handleOpenChange = (value: boolean) => {
    if (!value) setCurrentStep(0);
    onOpenChange(value);
  };

  useEffect(() => {
    if (!open) setCurrentStep(0);
  }, [open]);

  return {
    pageConfig,
    currentStep,
    childArray,
    isLastStep,
    isFirstStep,
    progress,
    canContinue,
    canGoBack,
    contextValue,
    handleCancel,
    handleBack,
    handleContinue,
    handleOpenChange,
  };
};

export const useWizard = (): UseWizardReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<WizardConfig>({});
  const [resolver, setResolver] = useState<((value: boolean) => void) | null>(
    null,
  );

  const openWizard = (wizardConfig: WizardConfig): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfig(wizardConfig);
      setIsOpen(true);
      setResolver(() => resolve);
    });
  };

  const handleComplete = () => {
    if (resolver) {
      resolver(true);
      setResolver(null);
      setIsOpen(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      if (resolver) resolver(false);
      setResolver(null);
    }
  };

  return {
    isOpen,
    openWizard,
    wizardProps: {
      open: isOpen,
      onOpenChange: handleOpenChange,
      children: config.children,
      onComplete: handleComplete,
      continueButtonText: config.defaultContinueButtonText,
      backButtonText: config.defaultBackButtonText,
      cancelButtonText: config.defaultCancelButtonText,
      completeButtonText: config.defaultCompleteButtonText,
    },
  };
};

// ASSETS
